import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

type LinkDecision = { internalTo?: string; externalTo?: string }

type NavNode = { label: string; href?: string; children: NavNode[] }
type NavSection = { title: string; children: NavNode[] }

export type DocsSidebarProps = {
  markdown: string
  decideLink?: (href: string) => LinkDecision | undefined
}

/** Parse a `- **[label](href)**` / `[label](href)` / plain list item into its parts. */
function parseContent(input: string): { label: string; href?: string } {
  const s = input.trim()
  let m = /^\*\*\[(.+?)\]\((.+?)\)\*\*$/.exec(s)
  if (m) return { label: m[1], href: m[2] }
  m = /^\[(.+?)\]\((.+?)\)$/.exec(s)
  if (m) return { label: m[1], href: m[2] }
  m = /^\*\*(.+?)\*\*$/.exec(s)
  if (m) return { label: m[1] }
  return { label: s.replace(/\*\*/g, '') }
}

/** Turn the wiki `_Sidebar.md` (h2 sections + indented lists) into a section/tree structure. */
function parseSidebar(md: string): { home?: { label: string; href?: string }; sections: NavSection[] } {
  const lines = md.split('\n')
  let home: { label: string; href?: string } | undefined
  const sections: NavSection[] = []
  let current: NavSection | null = null
  let stack: { indent: number; node: NavNode }[] = []

  for (const raw of lines) {
    if (!raw.trim()) continue
    const h2 = /^##\s+(.*)$/.exec(raw)
    if (h2) {
      current = { title: parseContent(h2[1]).label, children: [] }
      sections.push(current)
      stack = []
      continue
    }
    const h1 = /^#\s+(.*)$/.exec(raw)
    if (h1) {
      home = parseContent(h1[1])
      continue
    }
    const li = /^(\s*)[-*]\s+(.*)$/.exec(raw)
    if (li && current) {
      const indent = li[1].length
      const node: NavNode = { ...parseContent(li[2]), children: [] }
      while (stack.length && stack[stack.length - 1].indent >= indent) stack.pop()
      const parent = stack.length ? stack[stack.length - 1].node.children : current.children
      parent.push(node)
      stack.push({ indent, node })
    }
  }
  return { home, sections }
}

/**
 * Fixed navigation: full-height sticky rail with collapsible sections and
 * groups. Everything is expanded by default; the user can collapse branches. Scrolls independently
 * of the page content.
 */
export default function DocsSidebar({ markdown, decideLink }: DocsSidebarProps) {
  const location = useLocation()
  const { home, sections } = React.useMemo(() => parseSidebar(markdown), [markdown])

  const resolveTo = React.useCallback(
    (href?: string): string | undefined => {
      if (!href) return undefined
      const d = decideLink?.(href)
      const target = d?.internalTo ?? (/^(https?:)?\/\//i.test(href) ? undefined : href)
      return target?.split('#')[0]
    },
    [decideLink]
  )

  // Keys (section + ancestor groups) whose subtree contains the current route - kept open.
  const activeKeys = React.useMemo(() => {
    const keys = new Set<string>()
    const walk = (nodes: NavNode[], prefix: string): boolean => {
      let hit = false
      nodes.forEach((n, i) => {
        const k = `${prefix}.${i}`
        const isActive = resolveTo(n.href) === location.pathname
        const childHit = n.children.length ? walk(n.children, k) : false
        if (isActive || childHit) {
          if (n.children.length) keys.add(k)
          hit = true
        }
      })
      return hit
    }
    sections.forEach((section, si) => {
      if (walk(section.children, `s${si}`)) keys.add(`s${si}`)
    })
    return keys
  }, [sections, location.pathname, resolveTo])

  // Default: every section + nested group collapsed, except the first section and the active branch.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => {
    const set = new Set<string>()
    const walk = (nodes: NavNode[], prefix: string) =>
      nodes.forEach((n, i) => {
        const k = `${prefix}.${i}`
        if (n.children.length) {
          set.add(k)
          walk(n.children, k)
        }
      })
    sections.forEach((section, si) => {
      const skey = `s${si}`
      if (si !== 0) set.add(skey) // open the first section by default, collapse the rest
      walk(section.children, skey)
    })
    activeKeys.forEach((k) => set.delete(k))
    return set
  })

  // On navigation, make sure the active branch is expanded (without collapsing other user choices).
  React.useEffect(() => {
    setCollapsed((prev) => {
      if (![...activeKeys].some((k) => prev.has(k))) return prev
      const next = new Set(prev)
      activeKeys.forEach((k) => next.delete(k))
      return next
    })
  }, [activeKeys])

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const resolve = (href?: string): { to?: string; external?: string } => {
    if (!href) return {}
    const d = decideLink?.(href)
    if (d?.internalTo) return { to: d.internalTo }
    if (d?.externalTo) return { external: d.externalTo }
    return /^(https?:)?\/\//i.test(href) ? { external: href } : { to: href }
  }

  const renderNode = (node: NavNode, key: string, depth: number): React.ReactNode => {
    const hasChildren = node.children.length > 0
    const isOpen = !collapsed.has(key)
    const { to, external } = resolve(node.href)
    const active = to ? location.pathname === to.split('#')[0] : false

    if (hasChildren) {
      return (
        <li key={key} className="ds-item">
          <button
            type="button"
            className={`ds-row ds-group ${active ? 'is-active' : ''}`}
            style={{ paddingLeft: 10 + depth * 14 }}
            onClick={() => toggle(key)}
            aria-expanded={isOpen}
          >
            <ChevronRight size={14} className={`ds-chevron ${isOpen ? 'is-open' : ''}`} />
            <span className="ds-label">{node.label}</span>
          </button>
          {isOpen && <ul className="ds-list">{node.children.map((c, i) => renderNode(c, `${key}.${i}`, depth + 1))}</ul>}
        </li>
      )
    }

    const style = { paddingLeft: 10 + depth * 14 + 20 }
    return (
      <li key={key} className="ds-item">
        {to ? (
          <Link to={to} className={`ds-row ds-leaf ${active ? 'is-active' : ''}`} style={style}>
            <span className="ds-label">{node.label}</span>
          </Link>
        ) : external ? (
          <a href={external} target="_blank" rel="noreferrer" className="ds-row ds-leaf" style={style}>
            <span className="ds-label">{node.label}</span>
          </a>
        ) : (
          <span className="ds-row ds-leaf ds-muted" style={style}>
            <span className="ds-label">{node.label}</span>
          </span>
        )}
      </li>
    )
  }

  const homeLink = home && resolve(home.href)

  return (
    <nav className="ds-nav" aria-label="Documentation">
      {home && (
        homeLink?.to ? (
          <Link to={homeLink.to} className="ds-home">{home.label}</Link>
        ) : (
          <a href={homeLink?.external} target="_blank" rel="noreferrer" className="ds-home">{home.label}</a>
        )
      )}
      {sections.map((section, si) => {
        const key = `s${si}`
        const isOpen = !collapsed.has(key)
        return (
          <div key={key} className="ds-section">
            <button type="button" className="ds-section-head" onClick={() => toggle(key)} aria-expanded={isOpen}>
              <span>{section.title}</span>
              <ChevronRight size={14} className={`ds-chevron ${isOpen ? 'is-open' : ''}`} />
            </button>
            {isOpen && <ul className="ds-list">{section.children.map((c, i) => renderNode(c, `${key}.${i}`, 0))}</ul>}
          </div>
        )
      })}
    </nav>
  )
}
